import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { loadLogs, setParams } from 'state/modules/monitoring'
import { ROUTES } from '~/views/routes'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  Tooltip as MuiTooltip
} from '@material-ui/core'
import {
  ExpandMore,
  ExpandLess,
  InfoOutlined,
  Refresh
} from '@material-ui/icons'
import { Skeleton } from '@material-ui/lab'
import {
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  Chart as ChartJS
} from 'chart.js'
import { Bar as BarChart, Line as LineChart } from 'react-chartjs-2'
import { makeStyles } from '@material-ui/core/styles'
import * as moment from 'moment'
import { AppContainer } from 'views/shared'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend
)

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  periodLabel: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  kpiCard: {
    padding: theme.spacing(2),
    textAlign: 'center'
  },
  kpiValue: {
    fontWeight: 700,
    fontSize: '1.6rem'
  },
  kpiLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  chartContainer: {
    padding: theme.spacing(2),
    height: '100%'
  },
  tableContainer: {
    marginBottom: theme.spacing(3)
  },
  tableTitle: {
    padding: theme.spacing(2, 2, 1)
  },
  loaderCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200
  },
  infoIcon: {
    fontSize: '1rem',
    color: theme.palette.text.hint,
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'middle',
    cursor: 'default'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 2, 1)
  },
  exceptionRow: {
    backgroundColor: theme.palette.grey[50]
  },
  stackTrace: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    color: theme.palette.error.dark,
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    maxHeight: 300,
    overflowY: 'auto'
  }
}))

const errorRateColor = (rate) => {
  if (rate < 1) return 'green'
  if (rate <= 5) return '#f0a500'
  return 'red'
}

function InfoTip({ text }) {
  const classes = useStyles()
  return (
    <MuiTooltip title={text} placement='top' arrow>
      <InfoOutlined className={classes.infoIcon} />
    </MuiTooltip>
  )
}

function KpiCard({ label, tooltip, children }) {
  const classes = useStyles()
  return (
    <Paper className={classes.kpiCard}>
      <Box display='flex' justifyContent='center' alignItems='center'>
        <Typography className={classes.kpiValue}>{children}</Typography>
        <InfoTip text={tooltip} />
      </Box>
      <Typography className={classes.kpiLabel}>{label}</Typography>
    </Paper>
  )
}

function SortableTable({ title, tooltip, rows, columns }) {
  const classes = useStyles()
  const [orderBy, setOrderBy] = useState(columns[0].key)
  const [order, setOrder] = useState('asc')

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(key)
      setOrder('asc')
    }
  }

  const sorted = useMemo(() => {
    if (!rows) return []
    return [...rows].sort((a, b) => {
      const av = a[orderBy]
      const bv = b[orderBy]
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'string') {
        return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      }
      return order === 'asc' ? av - bv : bv - av
    })
  }, [rows, orderBy, order])

  return (
    <Paper className={classes.tableContainer}>
      <Box className={classes.sectionHeader}>
        <Typography variant='h6'>{title}</Typography>
        {tooltip && <InfoTip text={tooltip} />}
      </Box>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  <TableSortLabel
                    active={orderBy === col.key}
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={() => handleSort(col.key)}>
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, i) => (
              <TableRow key={i}>
                {columns.map((col) => (
                  <TableCell key={col.key}>
                    {col.render ? col.render(row[col.key]) : row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

const endpointColumns = [
  { key: 'Method', label: 'Method' },
  { key: 'Path', label: 'Path' },
  { key: 'RequestCount', label: 'Requests' },
  {
    key: 'AvgLatencyMs',
    label: 'Avg Latency (ms)',
    render: (v) => v?.toFixed(1)
  },
  {
    key: 'P95LatencyMs',
    label: 'P95 Latency (ms)',
    render: (v) => v?.toFixed(1)
  },
  { key: 'ErrorCount', label: 'Errors' }
]

const errorColumns = [
  { key: 'Path', label: 'Path' },
  { key: 'StatusCode', label: 'Status Code' },
  { key: 'Count', label: 'Count' }
]

function RecentExceptionsTable({ exceptions }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState({})

  const toggle = (i) => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))

  if (!exceptions?.length) return null

  return (
    <Paper className={classes.tableContainer}>
      <Box className={classes.sectionHeader}>
        <Typography variant='h6'>Recent Exceptions</Typography>
        <InfoTip text='Últimas excepciones registradas por la API. Hacé clic en el ícono para ver el stack trace completo.' />
      </Box>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 40 }} />
              <TableCell>Timestamp</TableCell>
              <TableCell>Path</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exceptions.map((ex, i) => (
              <React.Fragment key={i}>
                <TableRow hover>
                  <TableCell padding='none' align='center'>
                    <IconButton size='small' onClick={() => toggle(i)}>
                      {expanded[i] ? (
                        <ExpandLess fontSize='small' />
                      ) : (
                        <ExpandMore fontSize='small' />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>
                    {moment(ex.Timestamp).format('DD/MM HH:mm:ss')}
                  </TableCell>
                  <TableCell>{ex.Path}</TableCell>
                  <TableCell>{ex.Message}</TableCell>
                </TableRow>
                <TableRow className={classes.exceptionRow}>
                  <TableCell
                    colSpan={4}
                    style={{ padding: 0, borderBottom: 'none' }}>
                    <Collapse in={!!expanded[i]} timeout='auto' unmountOnExit>
                      <Box padding={2}>
                        <pre className={classes.stackTrace}>
                          {ex.ExceptionDetail}
                        </pre>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

export const MonitoringPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { logs, pending } = useSelector((state) => state.monitoring)
  const { user } = useSelector((state) => state.login)
  const allowedEmails = process.env.REACT_APP_CLOUDWATCH_ALLOWED_EMAILS
    ? process.env.REACT_APP_CLOUDWATCH_ALLOWED_EMAILS.split(',')
    : []
  const [hours, setHours] = useState(1)
  const [top, setTop] = useState(10)

  useEffect(() => {
    if (allowedEmails.includes(user?.name)) {
      dispatch(loadLogs(hours, top))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const handleSearch = () => {
    dispatch(setParams(hours, top))
    dispatch(loadLogs(hours, top))
  }

  const timeSeriesData = useMemo(() => {
    const pts = logs?.RequestsTimeSeries
    if (!pts?.length) return null
    const fmt = hours <= 6 ? 'HH:mm' : 'DD/MM HH:mm'
    return {
      labels: pts.map((p) => moment(p.Timestamp).format(fmt)),
      datasets: [
        {
          label: 'Requests',
          data: pts.map((p) => p.RequestCount),
          borderColor: 'rgba(63, 81, 181, 1)',
          backgroundColor: 'rgba(63, 81, 181, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: pts.length > 40 ? 0 : 3
        },
        {
          label: 'Errors',
          data: pts.map((p) => p.ErrorCount),
          borderColor: 'rgba(211, 47, 47, 1)',
          backgroundColor: 'rgba(211, 47, 47, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: pts.length > 40 ? 0 : 3
        }
      ]
    }
  }, [logs, hours])

  const timeSeriesOptions = useMemo(
    () => ({
      responsive: true,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top' },
        tooltip: {
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex
              const pt = logs?.RequestsTimeSeries?.[idx]
              return pt ? moment(pt.Timestamp).format('DD/MM/YYYY HH:mm') : ''
            }
          }
        }
      },
      scales: {
        x: { ticks: { maxTicksLimit: 12, maxRotation: 0 } },
        y: { beginAtZero: true }
      }
    }),
    [logs]
  )

  const chartData = useMemo(() => {
    if (!logs?.TopEndpoints) return null
    return {
      labels: logs.TopEndpoints.map((e) => `${e.Method} ${e.Path}`),
      datasets: [
        {
          label: 'Requests',
          data: logs.TopEndpoints.map((e) => e.RequestCount),
          backgroundColor: 'rgba(63, 81, 181, 0.7)'
        }
      ]
    }
  }, [logs])

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { display: false },
        title: { display: true, text: 'Top Endpoints por Request Count' },
        tooltip: {
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex
              const ep = logs?.TopEndpoints?.[idx]
              return ep ? `${ep.Method} ${ep.Path}` : ''
            },
            label: (item) => `Requests: ${item.raw}`
          }
        }
      },
      scales: {
        x: { ticks: { display: false } }
      }
    }),
    [logs]
  )

  if (!allowedEmails.includes(user?.name))
    return <Redirect to={ROUTES.PUBLIC.UNAUTHORIZED} />

  const formatPeriod = (From, To) => {
    const fmt = 'DD/MM/YYYY HH:mm'
    return `Período: ${moment(From).format(fmt)} – ${moment(To).format(fmt)} UTC`
  }

  return (
    <AppContainer title='Monitoring de API'>
      <div className={classes.root}>
        <div className={classes.headerRow}>
          <Button
            variant='outlined'
            startIcon={<Refresh />}
            onClick={handleSearch}
            disabled={pending}>
            Actualizar
          </Button>
        </div>

        <div className={classes.controlsRow}>
          <FormControl variant='outlined' size='small'>
            <InputLabel>Ventana</InputLabel>
            <Select
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              label='Ventana'
              style={{ minWidth: 140 }}>
              {[1, 2, 3, 6, 12, 24].map((h) => (
                <MenuItem key={h} value={h}>
                  {h === 1 ? 'Última hora' : `Últimas ${h} horas`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Top N'
            type='number'
            value={top}
            onChange={(e) => {
              const val = Math.min(100, Math.max(1, Number(e.target.value)))
              setTop(val)
            }}
            inputProps={{ min: 1, max: 100 }}
            variant='outlined'
            size='small'
            style={{ width: 100 }}
          />
          <Button
            variant='contained'
            color='primary'
            onClick={handleSearch}
            disabled={pending}>
            Buscar
          </Button>
        </div>

        {logs && (
          <Typography className={classes.periodLabel}>
            {formatPeriod(logs.From, logs.To)}
            {logs.LogGroupName && ` · ${logs.LogGroupName}`}
          </Typography>
        )}

        {pending ? (
          <>
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Grid item xs={12} sm={6} md key={i}>
                  <Skeleton variant='rect' height={80} />
                </Grid>
              ))}
            </Grid>
            <Skeleton
              variant='rect'
              height={280}
              style={{ marginBottom: 24 }}
            />
            <Skeleton
              variant='rect'
              height={300}
              style={{ marginBottom: 24 }}
            />
            <Skeleton
              variant='rect'
              height={200}
              style={{ marginBottom: 24 }}
            />
            <Skeleton variant='rect' height={150} />
            <Skeleton variant='rect' height={200} style={{ marginTop: 24 }} />
          </>
        ) : logs ? (
          <>
            <Grid container spacing={2} style={{ marginBottom: 8 }}>
              <Grid item xs={12} sm={6} md>
                <KpiCard
                  label='Total Requests'
                  tooltip='Número total de requests HTTP recibidas por la API en el período seleccionado.'>
                  {logs.TotalRequests?.toLocaleString()}
                </KpiCard>
              </Grid>
              <Grid item xs={12} sm={6} md>
                <KpiCard
                  label='Total Errors'
                  tooltip='Cantidad de respuestas con código de error HTTP 4xx o 5xx en el período.'>
                  {logs.TotalErrors?.toLocaleString()}
                </KpiCard>
              </Grid>
              <Grid item xs={12} sm={6} md>
                <Paper className={classes.kpiCard}>
                  <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'>
                    <Chip
                      label={`${logs.ErrorRatePercent?.toFixed(2)}%`}
                      style={{
                        backgroundColor: errorRateColor(logs.ErrorRatePercent),
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1rem',
                        height: 36
                      }}
                    />
                    <InfoTip text='Porcentaje de requests que resultaron en error. Verde < 1%, amarillo 1–5%, rojo > 5%.' />
                  </Box>
                  <Typography
                    className={classes.kpiLabel}
                    style={{ marginTop: 4 }}>
                    Error Rate %
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md>
                <KpiCard
                  label='Avg Latency'
                  tooltip='Tiempo de respuesta promedio de todos los endpoints en el período. Un valor alto puede indicar cuellos de botella.'>
                  {logs.OverallAvgLatencyMs?.toFixed(1)} ms
                </KpiCard>
              </Grid>
              <Grid item xs={12} sm={6} md>
                <KpiCard
                  label='P95 Latency'
                  tooltip='El 95% de los requests se respondieron en este tiempo o menos. Refleja la experiencia del usuario en el peor caso habitual (excluye el 5% más lento).'>
                  {logs.GlobalP95LatencyMs?.toFixed(1)} ms
                </KpiCard>
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginBottom: 8 }}>
              {timeSeriesData && (
                <Grid item xs={12} md={6}>
                  <Paper className={classes.chartContainer}>
                    <Box display='flex' alignItems='center' marginBottom={1}>
                      <Typography variant='h6'>
                        Requests a lo largo del tiempo
                      </Typography>
                      <InfoTip text='Volumen de requests y errores agrupados por intervalos de tiempo. El tamaño del intervalo varía según la ventana seleccionada (ej: 5 min para 1 hora, 60 min para 12 horas).' />
                    </Box>
                    <LineChart
                      data={timeSeriesData}
                      options={timeSeriesOptions}
                    />
                  </Paper>
                </Grid>
              )}
              {chartData && (
                <Grid item xs={12} md={timeSeriesData ? 6 : 12}>
                  <Paper className={classes.chartContainer}>
                    <Box display='flex' alignItems='center' marginBottom={1}>
                      <Typography variant='h6'>
                        Top Endpoints por Requests
                      </Typography>
                      <InfoTip text='Volumen de requests por endpoint en el período. Pasá el cursor sobre una barra para ver el detalle del método y path.' />
                    </Box>
                    <BarChart data={chartData} options={chartOptions} />
                  </Paper>
                </Grid>
              )}
            </Grid>

            <SortableTable
              title='Top Endpoints'
              tooltip='Endpoints con mayor tráfico. Incluye latencia promedio, P95 y cantidad de errores 4xx/5xx. Hacé clic en los encabezados para ordenar.'
              rows={logs.TopEndpoints}
              columns={endpointColumns}
            />

            <SortableTable
              title='Top Errors'
              tooltip='Combinaciones de path + código de estado HTTP con más errores en el período. Un mismo path puede aparecer varias veces si tiene múltiples códigos de error.'
              rows={logs.TopErrors}
              columns={errorColumns}
            />

            <RecentExceptionsTable exceptions={logs.RecentExceptions} />
          </>
        ) : (
          <Box className={classes.loaderCenter}>
            <Typography color='textSecondary'>
              Seleccioná una ventana de tiempo y presioná Buscar.
            </Typography>
          </Box>
        )}
      </div>
    </AppContainer>
  )
}

export default MonitoringPage
